/**
 * Data freshness: one row per data system — the newest source data we hold,
 * when ZEEFLEET last refreshed it, how often it is refreshed, and where it stands against its refresh schedule.
 * All values come from the feed-health read model (see useDataFreshness);
 * anything the API cannot state shows as Unknown, never a guess, and an
 * Unknown cadence is never shown as fresh.
 */

import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, Clock, HelpCircle, MinusCircle, RefreshCw } from 'lucide-react';
import { useDataFreshness } from '../../hooks/useDataFreshness';
import { useOrdersDailyProjection } from '../../hooks/useOrdersDailyProjection';
import { londonDateString } from '../../utils/ordersDailyPresentation';
import {
  buildFreshnessRows,
  formatAgeWords,
  formatCadence,
  formatUploadInterval,
  MANUAL_UPLOAD_EXPECTED_INTERVAL_MS,
  formatDayKey,
  formatInstant,
  newestDayWithData,
  type AgeBand,
  type FreshnessRow,
  type FreshnessState,
} from '../../utils/dataFreshness';
import { Badge, type BadgeTone } from '../ui/badge';

const STATE_STYLE: Record<FreshnessState, { label: string; tone: BadgeTone; Icon: typeof CheckCircle }> = {
  on_schedule: { label: 'Up to date', tone: 'success', Icon: CheckCircle },
  running_late: { label: 'Running late', tone: 'warning', Icon: Clock },
  overdue: { label: 'Overdue', tone: 'destructive', Icon: AlertTriangle },
  no_schedule: { label: 'No schedule', tone: 'neutral', Icon: MinusCircle },
  upload_due: { label: 'Upload due', tone: 'warning', Icon: Clock },
  unknown: { label: 'Unknown', tone: 'neutral', Icon: HelpCircle },
  not_configured: { label: 'Not connected', tone: 'neutral', Icon: MinusCircle },
  no_new_data: { label: 'No new data', tone: 'destructive', Icon: AlertTriangle },
};

/** Neutral age bands for feeds with no learned cadence: display groupings, not a verdict. */
const BAND_STYLE: Record<AgeBand, { label: string; tone: BadgeTone }> = {
  recent: { label: 'Recent', tone: 'neutral' },
  this_week: { label: 'This week', tone: 'neutral' },
  old: { label: 'Old', tone: 'warning' },
};

/** How and how often data arrives, in plain words. */
function updatesText(row: FreshnessRow): string {
  if (row.manualUpload) {
    return `Manual upload, ${formatUploadInterval(MANUAL_UPLOAD_EXPECTED_INTERVAL_MS).replace(/ upload$/i, '').toLowerCase()}`;
  }
  if (row.state === 'not_configured') return 'Not connected';
  if (row.cadenceMs == null) return 'Automatic, no regular schedule';
  return `Automatic, ${formatCadence(row.cadenceMs).toLowerCase()}`;
}

export default function DataFreshnessPanel({ demo = false }: { demo?: boolean }) {
  const freshness = useDataFreshness(!demo);
  // Re-evaluate ages every 30 s without refetching.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  // Orders figures are a stored projection: when it was computed is a second
  // clock beside the feed's last fetch.
  const today = londonDateString();
  const { projection } = useOrdersDailyProjection(today, today, { enabled: !demo, autoRefresh: false });
  const ordersComputedAt = projection?.computed_at ?? null;

  // Newest day with GPS positions, when the series was read: a read always has
  // one entry per day, so an empty series means the read failed (Unknown, not "no data").
  const telematicsNewest = useMemo(
    () => (freshness.telematicsActivity.length > 0
      ? {
        day: newestDayWithData(freshness.telematicsActivity, (d) => (d.positions ?? 0) > 0),
        windowDays: freshness.telematicsActivity.length,
      }
      : undefined),
    [freshness.telematicsActivity],
  );
  const rows = useMemo(
    () => (freshness.feeds
      ? buildFreshnessRows(freshness.feeds, freshness.enabledSources, now, telematicsNewest ? { telematics: telematicsNewest } : {})
      : null),
    [freshness.feeds, freshness.enabledSources, now, telematicsNewest],
  );

  const newestSourceData = (row: FreshnessRow): string => {
    // Day-grain only: the rollups give the newest day with data, not a time of day.
    if (row.id === 'telematics') {
      const day = newestDayWithData(freshness.telematicsActivity, (d) => (d.positions ?? 0) > 0);
      return day ? `${formatDayKey(day)} (day only)` : 'Unknown';
    }
    if (row.id === 'orders') {
      const day = newestDayWithData(freshness.ordersActivity, (d) => (d.orders ?? 0) > 0);
      return day ? `${formatDayKey(day)} (day only)` : 'Unknown';
    }
    return 'Unknown';
  };

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden" data-testid="data-freshness-panel">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-700/50 border-l-4 border-l-emerald-400">
        <div className="flex items-center gap-2">
          <RefreshCw className="w-5 h-5 text-emerald-400" />
          <h3 className="text-sm font-bold text-white uppercase tracking-wider">Data freshness</h3>
        </div>
        <span className="text-xs text-gray-400">
          {freshness.checkedAt ? `Checked ${formatInstant(freshness.checkedAt)}` : ''}
        </span>
      </div>

      {demo ? (
        <p className="px-5 py-4 text-sm text-gray-400">Freshness is not shown for demo data.</p>
      ) : freshness.loading ? (
        <p className="px-5 py-4 text-sm text-gray-400">Checking the data sources…</p>
      ) : !rows ? (
        <p className="px-5 py-4 text-sm text-amber-300">
          Could not check how up to date the data is, so every source shows Unknown.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-400">
                <th className="px-5 py-2 font-semibold">Source</th>
                <th className="px-3 py-2 font-semibold">Newest data</th>
                <th className="px-3 py-2 font-semibold">Last updated</th>
                <th className="px-3 py-2 font-semibold">Updates</th>
                <th className="px-5 py-2 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/60">
              {rows.map((row) => {
                const style = STATE_STYLE[row.state];
                return (
                  <tr key={row.id} data-testid={`freshness-row-${row.id}`}>
                    <td className="px-5 py-3">
                      <div className="text-white font-medium">{row.label}</div>
                      {row.sources.length > 0 && (
                        <div className="text-xs text-gray-400">
                          {row.sources.map((src) => src.name).join(', ')}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-gray-300">
                      {newestSourceData(row)}
                      {row.id === 'orders' && ordersComputedAt && (
                        <div className="text-xs text-gray-500">
                          Figures calculated {formatInstant(ordersComputedAt)}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-gray-300">
                      {row.manualUpload && (
                        <div className="text-xs text-gray-500">Last upload</div>
                      )}
                      {row.lastRefreshedAt ? (
                        <>
                          {formatInstant(row.lastRefreshedAt)}
                          <div className="text-xs text-gray-500">{formatAgeWords(row.lastRefreshedAt, now)}</div>
                        </>
                      ) : 'Unknown'}
                    </td>
                    <td className="px-3 py-3 text-gray-300">
                      {updatesText(row)}
                    </td>
                    <td className="px-5 py-3">
                      {row.state === 'no_schedule' && row.ageBand ? (
                        <Badge tone={BAND_STYLE[row.ageBand].tone}>
                          {BAND_STYLE[row.ageBand].label}
                        </Badge>
                      ) : (
                        <Badge tone={style.tone} icon={style.Icon}>
                          {style.label}
                        </Badge>
                      )}
                      {row.failingFeeds > 0 && (
                        <div className="text-xs text-red-300 mt-1">
                          {row.failingFeeds} of {row.expectedFeeds} sources failing to update
                        </div>
                      )}
                      {row.reason && row.state !== 'on_schedule' && (
                        <div className="text-xs text-gray-500 mt-1 max-w-64">{row.reason}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}
